import TwoColumnLayout from "layout/TwoColumnLayout";
import { useLoaderData } from "react-router";
import React from "react";
import { TrainDetailSection } from "train/TrainDetailSection";
import TrainOptionsPanel from "train/TrainOptionsPanel";
import { TrainActivityPanel } from "./activity/TrainActivityPanel";
import { TrainItinerarySidebarApp } from "show/ItinerarySidebar/app";
import moment from "moment";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateTrainsCache } from "utilities/api/trainQuery";
import { useIsInternal } from "common/SessionContext";

export const TrainDetailApp = () => {
  const isInternal = useIsInternal();
  const viewModel = useLoaderData<server.viewmodel.TrainDetailFrontModel>();
  const trainId = viewModel.sidePanelDto.train.trainId;
  const queryClient = useQueryClient();

  const sidePanelQuery = useQuery({
    queryKey: ["linkedShowsToFlight", "train", trainId],
    queryFn: async () => {
      const result = await window.CoreApi.Train.GetSidePanelData(trainId);
      if (result.data.hasErrors) throw new Error(result.data.errors[0].message);
      return result.data.data;
    },
    initialData: viewModel.sidePanelDto,
    initialDataUpdatedAt: Date.now(),
    staleTime: Infinity,
  });

  const { train, passengers, files, linkedShows } = sidePanelQuery.data;
  const refetchSidePanelData = async () => {
    await sidePanelQuery.refetch();
    invalidateTrainsCache(queryClient);
  };

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <TrainDetailSection
        train={train}
        linkedShows={linkedShows}
        linkShowToTravel={{
          canLink: train.permissions.canModify,
          travelType: "train",
          travelId: trainId,
          appointmentDateTime: train.dateTime,
          artist: train.artist,
          linkedShowIds: linkedShows.map(show => show.showId),
          onLinked: refetchSidePanelData
        }}
        travellers={passengers}
        files={files}
        onUpdated={refetchSidePanelData}
      />
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn>
      <TrainOptionsPanel
        optionsPanelData={viewModel.optionsPanel}
        listUrl={viewModel.listUrl}
        trainId={trainId}
      />
      {isInternal && <TrainItinerarySidebarApp
        trainId={trainId}
        artist={train.artist}
        startDate={moment.utc(train.dateTime.startDisplay)}
        endDate={moment.utc(train.dateTime.endDisplay)}
        linkedShowIds={linkedShows.map(ls => ls.showId)}
        queryKeys="trainItinerary"
      />}
      {isInternal && <TrainActivityPanel
        config={viewModel.activityPanelConfig}
        trainId={trainId}
      />}
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>
}
